import { motion } from "framer-motion";
import { NeuralBackground } from "./NeuralBackground";
import nithinImage from "@/assets/nithin.png";

const metrics = [
  { value: "94.29", label: "JEE Percentile", sub: "Top-tier analytical rigor" },
  { value: "92.88%", label: "Senior Secondary", sub: "Academic excellence" },
  { value: "Self-Taught", label: "AI Builder", sub: "Learning at the frontier" },
  { value: "Future", label: "SaaS Founder", sub: "Building at scale" },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.1 + i * 0.08, duration: 0.8, ease: [0.22, 1, 0.36, 1] as const },
  }),
};

export function Hero() {
  return (
    <section id="top" className="relative min-h-screen w-full overflow-hidden pt-32 pb-20">
      <NeuralBackground />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-12 px-6 lg:grid-cols-[1.3fr_1fr] lg:items-center">
        <div className="relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="glass mb-6 inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs text-muted-foreground"
          >
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-electric opacity-75" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-electric" />
            </span>
            Chapter 01 — In Motion
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="text-gradient text-[clamp(2.5rem,6vw,5.25rem)] font-semibold leading-[1.02] tracking-[-0.035em]"
          >
            Building Toward
            <br />
            The Next Generation
            <br />
            of <span className="text-electric-gradient italic font-medium">AI Products.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="mt-8 max-w-xl text-base text-muted-foreground sm:text-lg"
          >
            I am <span className="text-foreground">Nithin Devadiga</span>, an aspiring Software
            Engineer and future SaaS founder exploring the intersection of Artificial Intelligence,
            software systems, and business innovation.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.35 }}
            className="mt-10 flex flex-wrap items-center gap-3"
          >
            <a
              href="#manifesto"
              className="group relative inline-flex items-center gap-2 overflow-hidden rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground transition-all hover:scale-[1.02]"
            >
              <span className="relative z-10">Explore Journey</span>
              <span className="relative z-10 transition-transform group-hover:translate-x-0.5">→</span>
            </a>
            <a
              href="#contact"
              className="glass inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium transition-all hover:bg-white/10"
            >
              Connect
            </a>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="relative z-10 mx-auto w-full max-w-md"
        >
          <div className="relative">
            <div className="absolute -inset-6 rounded-[2rem] bg-[radial-gradient(circle_at_30%_20%,oklch(0.65_0.25_250/0.35),transparent_60%)] blur-2xl" />
            <div className="glass relative overflow-hidden rounded-[1.75rem] p-1.5">
              <img
                src={nithinImage}
                alt="Nithin Devadiga"
                className="w-full rounded-[1.4rem] object-cover"
                loading="eager"
              />
              <div className="absolute inset-1.5 rounded-[1.4rem] ring-1 ring-inset ring-white/10" />
            </div>
            <div className="glass absolute -bottom-4 -left-4 rounded-2xl px-4 py-3 text-xs">
              <div className="text-muted-foreground">Currently</div>
              <div className="font-medium">Exploring AI · Building</div>
            </div>
          </div>
        </motion.div>
      </div>

      <div className="relative mx-auto mt-20 grid max-w-7xl grid-cols-2 gap-3 px-6 lg:grid-cols-4">
        {metrics.map((m, i) => (
          <motion.div
            key={m.label}
            custom={i}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-50px" }}
            variants={fadeUp}
            className="group glass relative overflow-hidden rounded-2xl p-5 transition-all hover:bg-white/5"
          >
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-electric/40 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
            <div className="text-2xl font-semibold tracking-tight sm:text-3xl">{m.value}</div>
            <div className="mt-1 text-sm text-foreground/80">{m.label}</div>
            <div className="mt-2 text-xs text-muted-foreground">{m.sub}</div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
